import { useMemo, useState } from 'react'
import {
  legislatorDisplay,
  voteChoiceDisplay,
  voteMatchesFilter,
  voteResultLabel,
  voteResultTone,
} from '../../helpers'
import type { VoteSession } from '../../models'
import { filterVoteDetails } from './filters'
import { groupVoteDetailsByParty } from './grouping'
import type { PartyGroup } from './grouping'

export interface VoteDetailPanelProps {
  vote: VoteSession
}

export function VoteDetailPanel({ vote }: VoteDetailPanelProps) {
  const [filter, setFilter] = useState<'all' | 'favor' | 'contra' | 'abst' | 'no-participa'>('all')
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState<'party' | 'all'>('party')

  const total = vote.votes_for + vote.votes_against + vote.abstentions
  const forPct = total > 0 ? (vote.votes_for / total) * 100 : 0
  const againstPct = total > 0 ? (vote.votes_against / total) * 100 : 0
  const abstPct = 100 - forPct - againstPct

  const resultClass = voteResultTone(vote)
  const resultText = voteResultLabel(vote)

  const scopeLabel = vote.vote_scope === 'amendment' ? 'Norma específica' : 'Proyecto'
  const summaryCopy = `${vote.vote_type} · ${scopeLabel} · ${vote.date} · ${vote.votes_for} a favor, ${vote.votes_against} en contra, ${vote.abstentions} abst.`

  const filteredDetails = useMemo(
    () => filterVoteDetails(vote.details, filter, search),
    [vote.details, filter, search],
  )
  const partyGroups = useMemo(
    () => groupVoteDetailsByParty(filteredDetails),
    [filteredDetails],
  )

  return (
    <div className="vote-inline">
      <div className="vote-inline-header">
        <span className={`result-badge ${resultClass}`}>{resultText}</span>
        <span className="vote-summary-text">{summaryCopy}</span>
      </div>

      <div className="vote-counts-bar">
        <div className="bar-for" style={{ width: `${forPct}%` }}></div>
        <div className="bar-against" style={{ width: `${againstPct}%` }}></div>
        <div className="bar-abst" style={{ width: `${abstPct}%` }}></div>
      </div>

      <div className="vote-controls-row">
        <div className="view-toggle">
          <button
            className={`view-btn${viewMode === 'party' ? ' active' : ''}`}
            onClick={() => setViewMode('party')}
          >
            Por partido
          </button>
          <button
            className={`view-btn${viewMode === 'all' ? ' active' : ''}`}
            onClick={() => setViewMode('all')}
          >
            Todos
          </button>
        </div>

        <div className="filter-pills">
          {(
            [
              ['all', 'Todos'],
              ['favor', 'A favor'],
              ['contra', 'En contra'],
              ['abst', 'Abst.'],
              ['no-participa', 'No participa'],
            ] as const
          ).map(([f, label]) => (
            <button
              key={f}
              className={`filter-pill${filter === f ? ' active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="voter-search">
          <input
            type="text"
            placeholder="Buscar parlamentario…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {viewMode === 'party' ? (
        <PartyView groups={partyGroups} />
      ) : (
        <LegislatorGrid details={filteredDetails} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Party view
// ---------------------------------------------------------------------------

function PartyView({ groups }: { groups: PartyGroup[] }) {
  return (
    <div className="party-groups">
      {groups.map((group) => (
        <PartyGroupRow key={group.party} group={group} />
      ))}
    </div>
  )
}

function PartyGroupRow({ group }: { group: PartyGroup }) {
  const [expanded, setExpanded] = useState(false)

  const favorCount = group.members.filter((d) =>
    voteMatchesFilter(d.vote_choice, 'favor'),
  ).length
  const contraCount = group.members.filter((d) =>
    voteMatchesFilter(d.vote_choice, 'contra'),
  ).length
  const abstCount = group.members.filter((d) =>
    voteMatchesFilter(d.vote_choice, 'abst'),
  ).length
  const totalM = group.members.length

  const fPct = totalM > 0 ? (favorCount / totalM) * 100 : 0
  const cPct = totalM > 0 ? (contraCount / totalM) * 100 : 0
  const aPct = totalM > 0 ? (abstCount / totalM) * 100 : 0

  let summary: string
  if (contraCount > 0 || abstCount > 0) {
    const parts = [`${favorCount} a favor`]
    if (contraCount > 0) parts.push(`${contraCount} contra`)
    if (abstCount > 0) parts.push(`${abstCount} abst.`)
    summary = parts.join(' · ')
  } else {
    summary = `${favorCount} a favor`
  }

  return (
    <div className="party-group">
      <div
        className="party-group-header"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className={`party-chevron${!expanded ? ' collapsed' : ''}`}>▾</span>
        <span className="party-name">{group.party}</span>
        <div className="party-bar">
          <div className="bar-for" style={{ width: `${fPct}%` }}></div>
          <div className="bar-against" style={{ width: `${cPct}%` }}></div>
          <div className="bar-abst" style={{ width: `${aPct}%` }}></div>
        </div>
        <span className="party-count">{summary}</span>
      </div>
      <div
        className="legislators-row"
        style={{ display: expanded ? 'flex' : 'none' }}
      >
        {group.members.map((d) => {
          const [displayName, initials] = legislatorDisplay(
            d.first_name,
            d.last_name,
            d.legislator_name,
          )
          const [voteClass] = voteChoiceDisplay(d.vote_choice)
          return (
            <span key={d.legislator_name} className={`leg-chip ${voteClass}`}>
              <span className={`leg-avatar-sm ${voteClass}`}>{initials}</span>
              {displayName}
            </span>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// All-legislators flat grid
// ---------------------------------------------------------------------------

function LegislatorGrid({
  details,
}: {
  details: import('../../models').VoteDetail[]
}) {
  return (
    <div className="legislator-grid">
      {details.map((d) => {
        const last =
          d.last_name ??
          (d.legislator_name.split(',')[0] ?? '').trim()
        const firstChar =
          d.first_name != null && d.first_name.length > 0
            ? d.first_name[0]!
            : null
        const displayName =
          firstChar != null
            ? `${firstChar.toUpperCase()}. ${last}`
            : last
        const initials = `${firstChar ?? ' '}${last[0] ?? ' '}`.toUpperCase()
        const partyTag = d.party ?? ''
        const [voteClass, voteLabel] = voteChoiceDisplay(d.vote_choice)

        return (
          <div key={d.legislator_name} className="legislator-item">
            <div className={`leg-avatar-sm ${voteClass}`}>{initials}</div>
            <div className="leg-info">
              <span className="leg-name">{displayName}</span>
              <span className="leg-party-tag">{partyTag}</span>
            </div>
            <span className={`leg-vote-tag ${voteClass}`}>{voteLabel}</span>
          </div>
        )
      })}
    </div>
  )
}
